import AppComposedChart, { LeftYAxisLabel, RightYAxisLabel } from '@/components/AppComposedChart';
import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableControl from '@/components/AppTableControl';
import ChartMetricsSettings from '@/components/common/ChartMetricsSettings';
import ExcelExporter from '@/components/common/ExcelExporter';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getGameStats } from '@/services/revenue.service';
import { formatDate, formatNumber } from '@/utils/formatters';
import {
  SyncOutlined,
  // SettingOutlined
} from '@ant-design/icons';
import {
  Button,
  Space,
  Table,
  // Tooltip as AntTooltip, Dropdown, Checkbox
} from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { Bar, CartesianGrid, Legend, Line, Tooltip, XAxis, YAxis } from 'recharts';

const START_DATE = dayjs().startOf('week').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

function formatDataNumberToen(value) {
  if (value == null || value === '') return 0;
  const num = Number(value);
  if (isNaN(num)) return String(value);

  return num.toLocaleString('en-US'); 
}

const columns = [
  {
    title: 'Date',
    dataIndex: 'start_date',
    key: 'start_date',
    fixed: 'left',
    width: 80,
    render: (value) => (value ? formatDate(value, 'DD/MM/YYYY') : '-'),
  },
  {
    title: 'Free Mode',
    key: 'free_mode',
    children: [
      {
        title: 'User',
        dataIndex: 'free_users',
        key: 'free_users',
        render: (value) => (value > 0 ? formatNumber(value) : 0),
      },
      {
        title: 'Match',
        dataIndex: 'free_matches',
        key: 'free_matches',
        render: (value) => (value > 0 ? formatNumber(value) : 0),
      },
    ],
  },
  {
    title: 'Solo Mode',
    key: 'solo_mode',
    children: [
      {
        title: 'User',
        dataIndex: 'solo_users',
        key: 'solo_users',
        render: (value) => (value > 0 ? formatNumber(value) : 0),
      },
      {
        title: 'Match',
        dataIndex: 'solo_matches',
        key: 'solo_matches',
        render: (value) => (value > 0 ? formatNumber(value) : 0),
      },
    ],
  },
  {
    title: 'Multiplayer Mode',
    key: 'multiplayer_mode',
    children: [
      {
        title: 'User',
        dataIndex: 'multi_users',
        key: 'multi_users',
        render: (value) => (value > 0 ? formatNumber(value) : 0),
      },
      {
        title: 'Match',
        dataIndex: 'multi_matches',
        key: 'multi_matches',
        render: (value) => (value > 0 ? formatNumber(value) : 0),
      },
    ],
  },
  {
    title: 'Total',
    key: 'total',
    children: [
      {
        title: 'Unique User',
        dataIndex: 'total_users',
        key: 'total_users',
        render: (value) => (value > 0 ? formatNumber(value) : 0),
      },
      {
        title: 'Match',
        dataIndex: 'total_matches',
        key: 'total_matches',
        render: (value) => (value > 0 ? formatNumber(value) : 0),
      },
    ],
  },
  {
    title: 'Average Match per User',
    dataIndex: 'average_match_per_user',
    key: 'average_match_per_user',
    render: (value) => (value > 0 ? formatNumber(value) : 0),
  },
  {
    title: 'Win Match',
    dataIndex: 'win_matches',
    key: 'win_matches',
    render: (value) => (value > 0 ? formatNumber(value) : 0),
  },
  {
    title: 'Accuracy Rate (%)',
    dataIndex: 'accuracy_rate',
    key: 'accuracy_rate',
    render: (value) => (value > 0 ? `${value}%` : '0.00%'),
  },
  {
    title: 'Average Score',
    dataIndex: 'average_score',
    key: 'average_score',
    render: (value) => (value > 0 ? formatNumber(value) : 0),
  },
  {
    title: 'Average Used Heart',
    dataIndex: 'average_heart_used',
    key: 'average_heart_used',
    render: (value) => (value > 0 ? formatNumber(value) : 0),
  },
];
const topicAnswerColumnMapping = {
  id: 'ID',
  keyword: 'Keyword',
  fullword_en: 'Fullword (EN)',
  fullword_my: 'Fullword (MY)',
  total_attempts: 'Total Attempts',
  unique_users: 'Unique Users',
  correct_count: 'Correct Count',
  incorrect_count: 'Incorrect Count',
  accuracy_rate: 'Accuracy Rate (%)',
};
const gameStatsColumnMapping = {
  start_date: 'Date',
  free_users: 'Free Mode User',
  free_matches: 'Free Mode Match',
  solo_users: 'Solo Mode User',
  solo_matches: 'Solo Mode Match',
  multi_users: 'Multiplayer Mode User',
  multi_matches: 'Multiplayer Mode Match',
  total_users: 'Total Unique User',
  total_matches: 'Total Match',
  average_match_per_user: 'Average Match per User',
  win_matches: 'Win Match',
  accuracy_rate: 'Accuracy Rate (%)',
  average_score: 'Average Score',
  average_heart_used: 'Average Used Heart',
};
const gameStatsGroupedHeaders = [
  {
    title: 'Free Mode',
    children: [
      {
        title: 'User',
      },
      {
        title: 'Match',
      },
    ],
  },
  {
    title: 'Solo Mode',
    children: [
      {
        title: 'User',
      },
      {
        title: 'Match',
      },
    ],
  },
  {
    title: 'Multiplayer Mode',
    children: [
      {
        title: 'User',
      },
      {
        title: 'Match',
      },
    ],
  },
  {
    title: 'Total',
    children: [
      {
        title: 'User',
      },
      {
        title: 'Match',
      },
    ],
  },
  {
    title: 'Average Match per User',
    children: null,
  },
  {
    title: 'Win Match',
    children: null,
  },
  {
    title: 'Accuracy Rate (%)',
    children: null,
  },
  {
    title: 'Average Score',
    children: null,
  },
  {
    title: 'Average Used Heart',
    children: null,
  },
];

// Chart metrics configuration
const chartMetrics = [
  {
    key: 'total_matches',
    label: 'Total Matches',
    type: 'bar',
    yAxisId: 'left',
    color: '#dea108',
  },
  {
    key: 'total_users',
    label: 'Total Users',
    type: 'bar',
    yAxisId: 'left',
    color: '#6a9eeb',
  },
  {
    key: 'accuracy_rate',
    label: 'Accuracy Rate',
    type: 'line',
    yAxisId: 'right',
    color: '#ec6a89',
  },
];

function GameStats() {
  const [date, setDate] = useState([START_DATE, END_DATE]);
  const [visibleMetrics, setVisibleMetrics] = useState(chartMetrics.map((metric) => metric.key));
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getGameStats({
        start_date: date[0],
        end_date: date[1],
      }),
    ['game-stats', date]
  );

  // const handleDownload = () => {
  //   exportToExcel([], {
  //     fileName: `TopicAnswerReport_${date[0]}_${date[1]}.xlsx`,
  //     sheetName: 'Topic Answer Report',
  //     columnMapping: topicAnswerColumnMapping,
  //   });
  // };
  const handleRefresh = () => {
    refetch();
  };

  const dataWithTotals = useMemo(() => {
    if (!data || data.length === 0) return null;

    const start = dayjs(date[0]);
    const end = dayjs(date[1]);
    const dailyData = new Map();

    let current = start;
    while (current.isBefore(end) || current.isSame(end)) {
      const dateStr = current.format('YYYY-MM-DD');
      const dayData = {
        key: dateStr,
        start_date: dateStr,
        free_users: 0,
        free_matches: 0,
        solo_users: 0,
        solo_matches: 0,
        multi_users: 0,
        multi_matches: 0,
        total_users: 0,
        total_matches: 0,
        average_match_per_user: 0,
        win_matches: 0,
        accuracy_rate: '0.00',
        average_score: 0,
        average_heart_used: 0,
      };
      dailyData.set(dateStr, dayData);
      current = current.add(1, 'day');
    }

    const totalsRow = {
      free_users: 0,
      free_matches: 0,
      solo_users: 0,
      solo_matches: 0,
      multi_users: 0,
      multi_matches: 0,
      total_users: 0,
      total_matches: 0,
      average_match_per_user: 0,
      win_matches: 0,
      accuracy_rate: 0,
      accuracy_rate_sum: 0,
      average_score: 0,
      average_heart_used: 0,
      average_match_per_user_sum: 0,
    };

    data.forEach((item) => {
      const itemDate = dayjs(item.start_date).format('YYYY-MM-DD');
      if (dailyData.has(itemDate)) {
        const result = dailyData.get(itemDate);
        result.free_users = Number(item.free_users);
        result.free_matches = Number(item.free_matches);
        result.solo_users = Number(item.solo_users);
        result.solo_matches = Number(item.solo_matches);
        result.multi_users = Number(item.multi_users);
        result.multi_matches = Number(item.multi_matches);
        result.total_users = Number(item.total_users);
        result.total_matches = Number(item.solo_multi_matches);
        // result.total_matches =
        //   Number(item.solo_matches) + Number(item.multi_matches) * Number(item.multi_users);
        result.win_matches = Number(item.win_matches);
        result.average_match_per_user = Number(item.average_match_per_user);
        result.accuracy_rate = item.accuracy_rate;
        result.average_score = Number(item.average_score);
        result.average_heart_used = Number(item.average_heart_used);

        totalsRow.free_users += Number(item.free_users);
        totalsRow.free_matches += Number(item.free_matches);
        totalsRow.solo_users += Number(item.solo_users);
        totalsRow.solo_matches += Number(item.solo_matches);
        totalsRow.multi_users += Number(item.multi_users);
        totalsRow.multi_matches += Number(item.multi_matches);
        totalsRow.total_users = Number(item.total_users_all);
        totalsRow.total_matches += Number(item.solo_multi_matches);
        // Number(item.solo_matches) + Number(item.multi_matches) * Number(item.multi_users);
        totalsRow.win_matches += Number(item.win_matches);
        totalsRow.average_match_per_user_sum += Number(item.average_match_per_user);
        totalsRow.average_score += Number(item.average_score);
        totalsRow.accuracy_rate_sum += Number(item.accuracy_rate);
        // totalsRow.average_heart_used += Number(item.average_heart_used);

        // Recalculate derived metrics
      }
      let totalAvgScore = 0;
      let totalAvgUsedHeart = 0;
      let totalAvgMatchPerUser = 0;
      data.forEach((item) => {
        totalAvgScore += Number(item.average_score);
        totalAvgUsedHeart += Number(item.average_heart_used);
        totalAvgMatchPerUser += Number(item.average_match_per_user);
      });

      totalsRow.accuracy_rate = `${totalsRow.total_matches > 0 ? `${((totalsRow.win_matches/totalsRow.total_matches) * 100).toFixed(2)}%` : '0.00%'}`; // tổng win/tổng chơi
      // totalsRow.accuracy_rate = `-`;
      totalsRow.average_match_per_user = Math.floor(totalAvgMatchPerUser / dailyData.size) || 0;
      // totalsRow.total_users > 0 ? totalsRow.total_matches / totalsRow.total_users : 0;
      totalsRow.average_score = formatDataNumberToen((totalAvgScore / dailyData.size).toFixed(2)) || 0;
      totalsRow.average_heart_used =
        formatDataNumberToen((
          // totalAvgUsedHeart / data.filter((item) => item.average_heart_used !== '0').length
          totalAvgUsedHeart / dailyData.size
        ).toFixed(2)) || 0;
    });

    return {
      data: Array.from(dailyData.values()),
      totals: totalsRow,
    };
  }, [data, date]);

  const dataWithTotalsForExport = useMemo(() => {
    if (!dataWithTotals) return [];

    return [
      ...dataWithTotals.data,
      {
        start_date: 'Total',
        ...dataWithTotals.totals,
        accuracy_rate: dataWithTotals?.totals?.accuracy_rate.replace('%', '') || 0,
      },
    ];
  }, [dataWithTotals]);

  const dataChart = useMemo(() => {
    if (!dataWithTotals) return [];
    return dataWithTotals.data.map((item) => ({
      ...item,
      accuracy_rate: parseFloat(item.accuracy_rate),
    }));
  }, [dataWithTotals]);

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle
        title="Game Statistics"
        extra={
          // <Flex gap={8}>
          //   {isMobileSm ? (
          //     <>
          //       {/* <AntTooltip title="Topic Answer Report">
          //         <Button onClick={handleDownload} icon={<DownloadOutlined />} />
          //       </AntTooltip> */}
          //       <AntTooltip title="Sync">
          //         <Button onClick={handleRefresh} icon={<SyncOutlined />} />
          //       </AntTooltip>
          //     </>
          //   ) : (
          //     <>
          //       {/* <Button onClick={handleDownload} icon={<DownloadOutlined />}>
          //         Topic Answer Report
          //       </Button> */}
          //       <Button onClick={handleRefresh} icon={<SyncOutlined />}>
          //         Sync
          //       </Button>
          //     </>
          //   )}
          // </Flex>
          <Button
            onClick={handleRefresh}
            disabled={isLoading || isRefetching}
            icon={<SyncOutlined />}
          >
            Sync
          </Button>
        }
      />

      <AppTableControl
        noStyle
        hasSelect={false}
        date={date}
        setDate={setDate}
        dateLimit={{
          value: 7,
          unit: 'day',
        }}
      />

      <div className="flex items-center justify-between w-full">
        <h2 className="text-xl font-medium">Question Performance Overview</h2>
        <div className="flex items-center gap-1">
          <ExcelExporter
            data={dataWithTotalsForExport}
            fileName={`game_stats_overview_${date[0]}_${date[1]}.xlsx`}
            sheetName={`Game Stats Overview`}
            columnMapping={gameStatsColumnMapping}
            groupedHeaders={gameStatsGroupedHeaders}
          />

          <ChartMetricsSettings
            chartMetrics={chartMetrics}
            disabled={isLoading || isRefetching}
            title="Chart Metrics Settings"
            visibleMetrics={visibleMetrics}
            setVisibleMetrics={setVisibleMetrics}
          />
        </div>
      </div>
      <div className={`${isMobile ? 'h-[500px]' : 'h-[700px]'} w-full`}>
        <AppComposedChart data={dataChart || []} loading={isLoading || isRefetching}>
          <CartesianGrid stroke="#ccc" strokeDasharray="3 0" />
          <Tooltip
            formatter={(value, name, props) => {
              const formattedValue = name === 'accuracy_rate' ? `${value}%` : formatNumber(value);
              return [
                formattedValue,
                name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
              ];
            }}
            labelFormatter={(label) => formatDate(label, 'DD/MM/YYYY')}
          />
          <Legend
            layout="horizontal"
            verticalAlign="top"
            align="center"
            wrapperStyle={{ paddingBottom: 8, fontSize: 12 }}
            formatter={(value) => {
              return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
            }}
          />
          <XAxis
            dataKey="start_date"
            label={{
              value: 'Date',
              position: 'bottom',
              offset: -10,
              style: {
                fontWeight: 600,
                fontSize: 10,
              },
            }}
            tick={{ fontSize: isMobile ? 9 : 10 }}
            tickFormatter={(value) => (value ? formatDate(value, 'DD/MM') : '')}
          />
          <YAxis
            yAxisId="left"
            label={<LeftYAxisLabel value="Attempts/Users" angle={-90} offset={10} />}
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => formatNumber(value)}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            label={<RightYAxisLabel value="Accuracy Rate (%)" angle={90} offset={20} />}
            tick={{ fontSize: 10 }}
            // tickFormatter={(value) => `${Math.round(value * 100)}`}
          />
          {chartMetrics.map((metric) => {
            if (!visibleMetrics.includes(metric.key)) return null;

            if (metric.type === 'bar') {
              return (
                <Bar
                  key={metric.key}
                  yAxisId={metric.yAxisId}
                  dataKey={metric.key}
                  fill={metric.color}
                />
              );
            } else if (metric.type === 'line') {
              return (
                <Line
                  key={metric.key}
                  yAxisId={metric.yAxisId}
                  type="linear"
                  dataKey={metric.key}
                  stroke={metric.color}
                />
              );
            }
            return null;
          })}
        </AppComposedChart>
      </div>

      <AppTable
        tableTitle={<p className="font-semibold">User Play Statistic</p>}
        columns={columns}
        dataSource={dataWithTotals?.data || []}
        hasPagination={false}
        loading={isLoading || isRefetching}
        // totals={totals}
        // onChange={handleTableChange}
        // rowKey={(record) => record.package_code}
        disableColumnVisibility={true}
        summary={() => {
          const summaryCols = [
            { key: 'free_users', label: 'Free Mode User' },
            { key: 'free_matches', label: 'Free Mode Match' },
            { key: 'solo_users', label: 'Solo Mode User' },
            { key: 'solo_matches', label: 'Solo Mode Match' },
            { key: 'multi_users', label: 'Multiplayer Mode User' },
            { key: 'multi_matches', label: 'Multiplayer Mode Match' },
            { key: 'total_users', label: 'Total Unique User' },
            { key: 'total_matches', label: 'Total Match' },
            { key: 'average_match_per_user', label: 'Avg Match per User' },
            { key: 'win_matches', label: 'Win Match' },
            { key: 'accuracy_rate', label: 'Accuracy Rate (%)' },
            { key: 'average_score', label: 'Avg Score' },
            { key: 'average_heart_used', label: 'Avg Used Heart' },
          ];

          const values = summaryCols.map((col) =>
            dataWithTotals?.totals ? dataWithTotals?.totals[col.key] : 0
          );

          return (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} align="center" className="font-semibold">
                  Total
                </Table.Summary.Cell>
                {values.map((value, index) => (
                  <Table.Summary.Cell
                    key={index + 1}
                    index={index + 1}
                    align="center"
                    className="font-semibold"
                  >
                    {typeof value === 'number' ? formatNumber(value) : value}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </Space>
  );
}

export default GameStats;
